import PureButton from '$store/components/ui/PureButton.tsx'
import { formatPrice, formatShippingEstimate } from '$store/sdk/format.ts'
import v from '$store/sdk/v.ts'
import { Signal, useSignal } from '@preact/signals'
import { useCart } from 'apps/vtex/hooks/useCart.ts'
import type {
    PickupPoint,
    SelectedSla,
    SimulationOrderForm,
    SKU,
    Sla,
} from 'apps/vtex/utils/types.ts'
import { CheckoutStore } from 'site/components/checkout/CheckoutStore.ts'
import c from 'site/sdk/c.ts'
import { useCallback, useEffect, useRef } from 'preact/hooks'
import { isLocker } from 'site/components/checkout/Shipping/utils/isLocker.ts'
import { findMinorDistance } from 'site/components/checkout/Shipping/utils/findMinorDistance.ts'
import Icon from 'site/components/ui/Icon.tsx'

export interface ShippingTexts {
    /**
     * @title Label do calcular CEP
     */
    label: string
    /**
     * @title Placeholder do CEP
     */
    placeholder: string
    /**
     * @title Texto do botão simular CEP
     */
    button: string
    /**
     * @title Texto do não sei meu CEP
     */
    dontKnowCEP: string
}

interface SlaMatcher {
    label: string
    regexList: RegExp[]
    compareSlas?: (a: Sla, b: Sla) => number
}

export interface Props {
    items: Array<SKU>
    texts: ShippingTexts
    variant?: 'checkout' | 'open' | 'closed'
    renderOnlyShipping?: boolean
    /** when false, don't render the full shipping methods table (useful for minicart) */
    showMethods?: boolean
}

const SLA_MATCHERS: SlaMatcher[] = [
    {
        label: 'Expresso',
        regexList: [/^(EXPRESSO)/i],
    },
    {
        label: 'Torra Express',
        regexList: [/^(TORRA EXPRESS)/i],
    },
    {
        label: 'Correios',
        regexList: [/^(CORREIOS)/i],
    },
    {
        label: 'Normal',
        regexList: [/^(NORMAL)/i],
    },
    {
        label: 'Retire em Locker',
        regexList: [/^(Clique Retire).+/i],
    },
    {
        label: 'Lojas Torra $1',
        regexList: [/^Lojas Torra_(.+) \(.+/i],
        compareSlas: (a, b) => {
            const bdA = a?.pickupDistance || 0
            const bdB = b?.pickupDistance || 0

            return bdA - bdB
        },
    },
]

const getSlasMapDelivery = (slas: Sla[]) => {
    const slasMapDelivery = new Map<string, Sla>()

    slas.filter((sla) => sla.deliveryChannel === 'delivery').forEach((sla) => {
        const regex = SLA_MATCHERS.find(({ regexList }) =>
            regexList.some((regex) => regex.test(sla.id))
        )

        if (slasMapDelivery.has(sla.id)) {
            const oldSla = slasMapDelivery.get(sla.id)!

            slasMapDelivery.set(sla.id, {
                ...oldSla,
                name: regex
                    ? sla.id.replace(regex.regexList[0], regex.label) as SelectedSla
                    : sla.name,
                price: oldSla.price + sla.price,
            })
        } else {
            slasMapDelivery.set(sla.id, {
                ...sla,
                name: regex
                    ? sla.id.replace(regex.regexList[0], regex.label) as SelectedSla
                    : sla.name,
            })
        }
    })

    return Array.from(slasMapDelivery.values())
}

const getSlasMapPickup = (slas: Sla[], pickupPoints: PickupPoint[]) => {
    const pickupsMap = new Map<string, Sla>()

    slas.filter((sla) => sla.deliveryChannel === 'pickup-in-point')
    2
    slas.forEach((sla) => {
        if (pickupsMap.has(sla.id)) {
            const oldSla = pickupsMap.get(sla.id)!

            pickupsMap.set(sla.id, {
                ...oldSla,
                listPrice: oldSla.listPrice + sla.listPrice,
                price: oldSla.price + sla.price,
            })
        } else {
            pickupsMap.set(sla.id, sla)
        }
    })

    const pickUps = Array.from(pickupsMap.values())

    if (!pickUps.length) {
        return {
            shops: [],
            lockers: [],
        }
    }

    const pickupShop = pickupPoints.filter(
        (i) => !isLocker(i.friendlyName),
    )
    const pickupLocker = pickupPoints.filter((i) => isLocker(i.friendlyName))

    const pickupShopWithSla = pickupShop?.map((shop) => {
        const sla = pickUps.find((pickUp) => pickUp.pickupPointId === shop.id)!
        const regex = SLA_MATCHERS.find(({ regexList }) =>
            regexList.some((regex) => regex.test(sla.id))
        )

        return {
            ...shop,
            sla: {
                ...sla,
                name: regex
                    ? sla.id.replace(regex.regexList[0], regex.label) as SelectedSla
                    : sla.name,
            },
        }
    })

    const pickupLockerWithSla = pickupLocker?.map((locker) => {
        const sla = pickUps.find((p) => p.pickupPointId === locker.id)!
        const regex = SLA_MATCHERS.find(({ regexList }) =>
            regexList.some((regex) => regex.test(sla.id))
        )

        return {
            ...locker,
            sla: {
                ...sla,
                name: regex
                    ? sla.id.replace(regex.regexList[0], regex.label) as SelectedSla
                    : sla.name,
            },
        }
    })

    return {
        shops: pickupShopWithSla ?? [],
        lockers: pickupLockerWithSla ?? [],
    }
}

function ShippingContent(
    { simulation, renderOnlyShipping }: {
        simulation: Signal<SimulationOrderForm | null>
        renderOnlyShipping?: boolean
    },
) {
    const { cart } = useCart()

    const slas = simulation.value?.logisticsInfo.flatMap((i) => i.slas) ?? []

    const slasMapDelivery = getSlasMapDelivery(slas)
    const { shops, lockers } = getSlasMapPickup(slas, simulation.value?.pickupPoints ?? [])
    const shopMinorDistance = findMinorDistance(shops)
    const lockerMinorDistance = findMinorDistance(lockers)

    let methods = [...slasMapDelivery, shopMinorDistance.sla, lockerMinorDistance.sla].filter(
        Boolean,
    )

    if (renderOnlyShipping) {
        methods = methods.filter((method) => method.deliveryChannel !== 'pickup-in-point')
    }

    const locale = cart.value?.clientPreferencesData.locale || 'pt-BR'
    const currencyCode = cart.value?.storePreferencesData.currencyCode || 'BRL'

    if (simulation.value == null) {
        return null
    }

    if (methods.length === 0) {
        return (
            <div class='p-2'>
                <span>CEP inválido</span>
            </div>
        )
    }

    return (
        <table class='w-full text-neutral-1 text-sm mt-5 border-spacing-y-4 border-separate'>
            <tbody>
                {methods.map((method) => (
                    <tr>
                        <td className='max-w-[100px] capitalize'>
                            <span
                                className={method.name.toLowerCase() === 'turbo'
                                    ? 'font-black text-primary-700 thunderbolt'
                                    : ''}
                            >
                                {method.name.toLowerCase()}
                            </span>
                        </td>
                        <td class='max-w-[100px] text-center'>
                            {formatShippingEstimate(method.shippingEstimate)}
                        </td>
                        <td class='max-w-[100px] text-center'>
                            {method.price === 0
                                ? 'Grátis'
                                : formatPrice(method.price / 100, currencyCode, locale)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

function ShippingSimulation({ items, texts, variant, renderOnlyShipping, showMethods = true }: Props) {
    const postalCode = useSignal('')
    const open = useSignal(false)
    const loading = useSignal(false)
    const simulateResult = useSignal<SimulationOrderForm | null>(null)
    const { simulate } = useCart()
    const { country } = CheckoutStore

    const errorMessage = useSignal('')
    const variantOpenAndClosed = variant === 'open' || variant === 'closed'
    // Stable key so we don't stale-close over the initially selected SKU
    const itemsKey = items.map(({ id, seller, quantity }) => `${id}-${seller}-${quantity}`).join('|')
    const prevItemsKey = useRef(itemsKey)

    const vv = v<string>(
        (i) => !i && 'Insira um CEP',
        (i) => i.length !== 9 && 'O CEP deve conter 8 dígitos',
    )

    // Clear previous simulation when the selected SKU/items change (not on mount)
    useEffect(() => {
        if (prevItemsKey.current === itemsKey) return
        prevItemsKey.current = itemsKey
        simulateResult.value = null
        CheckoutStore.simulateResult.value = null
        CheckoutStore.shippingPrice.value = null
    }, [itemsKey])

    const handleSimulation = useCallback(async () => {
        const err = vv(postalCode.value)

        if (err) {
            errorMessage.value = err
            return
        }

        try {
            loading.value = true
            simulateResult.value = await simulate({
                items,
                postalCode: postalCode.value,
                country: country.value,
            })

            // Update CheckoutStore so other components (minicart, checkout) can read the simulate result
            CheckoutStore.simulateResult.value = simulateResult.value

            // Compute minimal delivery price (in cents) among delivery slas and set shippingPrice
            const deliveries = simulateResult.value?.logisticsInfo
                .flatMap((i) => i.slas)
                .filter((s) => s.deliveryChannel === 'delivery') ?? []

            if (deliveries.length > 0) {
                const min = deliveries.reduce((acc, cur) => (cur.price < acc ? cur.price : acc), deliveries[0].price)
                CheckoutStore.shippingPrice.value = min
            } else {
                CheckoutStore.shippingPrice.value = null
            }
        } finally {
            loading.value = false
        }
    }, [itemsKey])

    return (
        <div class={c('cep-option flex flex-col gap-2', variant === 'checkout' ? '' : 'mt-6')}>
            <form
                class={c(
                    'flex items-center',
                    variant === 'checkout' ? 'gap-x-2' : 'gap-x-2 lg:gap-x-5',
                    variantOpenAndClosed ? 'flex-col !items-start' : '',
                )}
                onSubmit={(e) => {
                    e.preventDefault()
                    handleSimulation()
                }}
            >
                {variant !== 'checkout' && (
                    <div
                        class={c(
                            'justify-between flex items-center gap-x-2 text-sm font-bold text-neutral-1 whitespace-nowrap',
                            variantOpenAndClosed && 'mb-3 w-full',
                        )}
                    >
                        <span>{texts.label ?? ''}</span>

                        {variant === 'closed' && (
                            <button
                                type='button'
                                class='flex items-center gap-1 text-sm italic font-bold text-primary-700'
                                onClick={() => {
                                    open.value = !open.value
                                }}
                            >
                                {open.value
                                    ? <Icon id='Close-Shipping' width={10} height={10} />
                                    : <Icon id='Calculator' width={10.5} height={13} />}
                                <span class='underline'>
                                    {open.value ? 'Fechar' : 'Calcular'}
                                </span>
                            </button>
                        )}
                    </div>
                )}

                <div
                    class={c(
                        variantOpenAndClosed ? 'w-full flex gap-x-2' : 'contents',
                        variant === 'closed' && !open.value ? 'hidden' : '',
                    )}
                >
                    <div class='relative w-full'>
                        {/* Popover with error message */}
                        <div
                            class={`pointer-events-none absolute left-1/2 w-full -translate-x-1/2 rounded transition-all z-20 ${
                                errorMessage.value ? '-top-20 opacity-100' : '-top-16 opacity-0'
                            }`}
                        >
                            <span class='grid min-h-10 py-2 rounded place-items-center text-center bg-[#707070] text-white'>
                                {errorMessage.value}
                            </span>
                            <div class='absolute left-1/2
                                h-0 w-0
                                -translate-x-1/2 border-l-[10px]
                                border-r-[10px] border-t-[16px]
                                border-l-transparent border-r-transparent border-t-[#707070]
                            ' />
                        </div>
                        {/* --- Popover with error message */}

                        <input
                            type='text'
                            class={c(
                                'cep-input bg-transparent px-5 text-sm text-[#9C9C9C] placeholder:text-neutral-3 outline-none',
                                'outline-offset-2 border',
                                errorMessage.value ? 'border-red-500' : 'border-neutral-4',
                                variant === 'checkout'
                                    ? 'w-44 bg-white h-12 rounded focus-within:outline-2 focus-within:outline-primary-700'
                                    : 'w-full h-11 focus-within:outline-2 focus-within:outline-[#707070]',
                            )}
                            placeholder={texts.placeholder}
                            value={postalCode.value ?? ''}
                            maxLength={9}
                            size={9}
                            onChange={(e: { currentTarget: { value: string } }) => {
                                e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '')
                                    .replace(/^(\d{5})(\d)/, '$1-$2')

                                postalCode.value = e.currentTarget.value

                                errorMessage.value = ''
                            }}
                            aria-label='Simular entrega, digite seu CEP'
                        />
                    </div>
                    {CheckoutStore.shippingPrice.value == null
                        ? (
                            <PureButton
                                type='submit'
                                loading={loading.value}
                                class={c(
                                    variant === 'checkout'
                                        ? 'h-12 bg-primary-700 w-20 text-sm shrink-0 rounded'
                                        : 'cep-simular bg-neutral-1 hover:bg-neutral-0 max-w-[100px] h-10 w-full',
                                    'transition-colors font-bold text-white',
                                    variantOpenAndClosed ? '!h-10' : '',
                                )}
                            >
                                {texts.button ?? ''}
                            </PureButton>
                        )
                        : (
                            <PureButton
                                type='button'
                                loading={false}
                                class={c(
                                    variant === 'checkout'
                                        ? 'h-12 bg-danger-700 w-20 text-sm shrink-0 rounded'
                                        : 'cep-simular bg-neutral-1 hover:bg-neutral-0 max-w-[100px] h-10 w-full',
                                    'transition-colors font-bold text-white',
                                    variantOpenAndClosed ? '!h-10' : '',
                                )}
                                onClick={() => {
                                    // Clear postal code, simulation results and error message
                                    postalCode.value = ''
                                    simulateResult.value = null
                                    CheckoutStore.simulateResult.value = null
                                    CheckoutStore.shippingPrice.value = null
                                    errorMessage.value = ''
                                }}
                            >
                                Remover
                            </PureButton>
                        )}
                </div>
            </form>

            {variant !== 'checkout' && (
                <div class={c('contents', variant === 'closed' && !open.value ? '!hidden' : '')}>
                    {texts.dontKnowCEP && (
                        <a
                            href='https://buscacepinter.correios.com.br/app/endereco/index.php'
                            class='mt-1 flex items-center gap-x-1.5 text-xs font-bold text-neutral-1 underline underline-offset-4 italic ml-auto'
                        >
                            {texts.dontKnowCEP}
                        </a>
                    )}

                    {showMethods !== false && (
                        <ShippingContent
                            simulation={simulateResult}
                            renderOnlyShipping={renderOnlyShipping}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default ShippingSimulation